/**
 * 리뷰 목록 공통 Mixin
 * - 검색, 페이징, 로딩 상태 관리 로직 공통화
 * - hearing-test, free-trial, real-usage 페이지에서 사용
 */

export default {
  data() {
    return {
      reviews: [],
      loading: false,
      error: null,

      // 페이징 상태
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,

      // 검색 상태
      searchKeyword: '',

      // UI 상태
      hasMore: false
    };
  },
  async created() {
    await this.loadReviews();
  },
  methods: {
    /**
     * 리뷰 목록 조회 (서버 페이지네이션)
     * - 자식 컴포넌트에서 fetchAPI 메서드를 반드시 구현해야 함
     * @param {boolean} isLoadMore - 더보기 여부 (true: 누적, false: 초기화)
     */
    async loadReviews(isLoadMore = false) {
      if (this.loading) return;

      // fetchAPI 메서드가 정의되어 있는지 확인
      if (typeof this.fetchAPI !== 'function') {
        console.error('fetchAPI method must be implemented in the component');
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        };

        // 검색어가 있으면 추가
        if (this.searchKeyword) {
          params.searchtext = this.searchKeyword;
        }

        const response = await this.fetchAPI(params);
        const newReviews = this.transformReviewData(response.data);

        // 더보기인 경우 기존 데이터에 추가, 아니면 교체
        if (isLoadMore) {
          this.reviews = [...this.reviews, ...newReviews];
        } else {
          this.reviews = newReviews;
        }

        // 전체 개수는 응답 헤더에서 가져옴
        this.totalCount = parseInt(response.headers['x-total-count'] || 0);

        // 더 불러올 데이터가 있는지 확인
        this.hasMore = this.reviews.length < this.totalCount;

      } catch (error) {
        this.error = '데이터를 불러오는 중 오류가 발생했습니다.';
        console.error('===== API 호출 에러 =====');
        console.error('에러:', error);
        console.error('에러 상세:', error.response?.data || error.message);
        console.error('========================');
      } finally {
        this.loading = false;
      }
    },

    /**
     * API 응답 데이터를 UI용 데이터로 변환
     */
    transformReviewData(reviews) {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

      // 본문(contents) HTML에 인라인으로 박힌 첫 번째 <img>의 src 추출
      // (파일 첨부 없이 에디터에 이미지를 붙여넣은 후기의 썸네일 폴백용)
      const firstImgInContents = (html) => {
        if (!html) return '';
        const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
        return m ? m[1] : '';
      };

      return reviews.map(review => {
        let mainThumbnail = '';
        const images = [];
        const attachments = [];

        if (review.attachments && review.attachments.length > 0) {
          review.attachments.forEach(att => {
            const ext = att.originalname ?
              att.originalname.substring(att.originalname.lastIndexOf('.')).toLowerCase() : '';

            if (imageExtensions.includes(ext)) {
              if (!mainThumbnail && att.type === 'main') {
                mainThumbnail = att.download_url;
              }
              images.push(att.download_url);
            } else {
              attachments.push({
                url: att.download_url,
                name: att.originalname,
                size: att.size
              });
            }
          });
        }

        return {
          id: review.id,
          thumbnail: mainThumbnail || images[0] || firstImgInContents(review.contents) || '',
          storeName: review.store_name || '', // '매장명'
          title: review.title,
          contents: review.contents,
          images: images,
          attachments: attachments,
          originalAttachments: review.attachments || []
        };
      });
    },

    /**
     * 더보기 버튼 클릭 핸들러
     */
    async loadMoreReviews() {
      if (!this.hasMore || this.loading) return;

      this.currentPage += 1;
      await this.loadReviews(true);
    },

    /**
     * 검색 핸들러
     * @param {string} keyword - 검색어
     */
    async handleSearch(keyword) {
      // 검색어 저장
      this.searchKeyword = keyword;

      // 페이지 초기화
      this.currentPage = 1;
      this.reviews = [];

      // 검색 조건으로 재조회
      await this.loadReviews(false);
    },

    /**
     * 리뷰 카드 클릭 핸들러
     * - 자식 컴포넌트에서 reviewType을 반드시 정의해야 함
     */
    handleReviewClick(id) {
      if (!this.reviewType) {
        console.error('reviewType must be defined in the component');
        return;
      }
      this.$router.push(`/reviews/${this.reviewType}/${id}`);
    }
  }
};